import { uniqueId } from '@microsoft/fast-web-utilities';
import { accordionItemStyles } from './accordion-item.styles';
import { attr, FASTElement, nullableNumberConverter } from '@microsoft/fast-element';
import { accordionItemTemplate } from './accordion-item.template';
import { StartEnd } from '../start-end/start-end';
import { applyMixins } from '../../foundation/apply-mixins';
import { NatGenDesignSystem } from '../../natgen-design-system';
import { CSSClassBehavior } from '../../decorators/class-list-decorator';

/**
 * An individual item in an {@link @microsoft/fast-foundation#(FASTAccordion:class) }.
 *
 * @element natgen-accordion-item
 * @slot start - Content which can be provided between the heading and the icon
 * @slot end - Content which can be provided between the start slot and icon
 * @slot heading - Content which serves as the accordion item heading and text of the expand button
 * @slot - The default slot for accordion item content
 * @slot expand-collapse-icon - The expanded / collapsed icon
 * @fires change - Fires a custom 'change' event when the button is invoked
 * @csspart heading - Wraps the button
 * @csspart button - The button which serves to invoke the item
 * @csspart heading-content - Wraps the slot for the heading content within the button
 * @csspart expand-collapse-icon - The icon container
 * @csspart region - The wrapper for the accordion item content
 *
 * @public
 */
export class NatGenAccordionItem extends FASTElement {
    connectedCallback(): void {
        super.connectedCallback();
        this.$fastController.addBehavior(new CSSClassBehavior('expanded', 'expanded'));

        this.id = this.id || uniqueId('accordion-item-');
    }

    /**
     * The color palette for the background of the accordion item.
     */
    @attr
    public color: string;

    /**
         * Configures the {@link https://www.w3.org/TR/wai-aria-1.1/#aria-level | level} of the
         * heading element.
         *
         * @defaultValue 2
         * @public
         * @remarks
         * HTML attribute: heading-level
         */
    @attr({
        attribute: 'heading-level',
        mode: 'fromView',
        converter: nullableNumberConverter,
    })
    public headinglevel: 1 | 2 | 3 | 4 | 5 | 6 = 2;

    /**
     * Expands or collapses the item.
     *
     * @public
     * @remarks
     * HTML attribute: expanded
     */
    @attr({ mode: 'boolean' })
    public expanded: boolean = false;
    public expandedChanged(oldValue: boolean, newValue: boolean): void {
        this.$emit(newValue ? 'expand' : 'collapse');
    }

    /**
     * Disables an accordion item
     *
     * @public
     * @remarks
     * HTML attribute: disabled
     */
    @attr({ mode: 'boolean' })
    public disabled: boolean = false;

    /**
     * @internal
     */
    public expandbutton: HTMLElement;

    /**
     * @internal
     */
    public clickHandler = (e: MouseEvent) => {
        if (this.disabled) {
            return;
        }

        this.$emit('click', e);
    };
}

export interface NatGenAccordionItem extends StartEnd {}
applyMixins(NatGenAccordionItem, StartEnd);

export const natGenAccordionItemDefinition = NatGenAccordionItem.compose({
    name: `${NatGenDesignSystem.prefix}-accordion-item`,
    template: accordionItemTemplate({
        collapsedIcon: 'plus',
        expandedIcon: 'minus'
    }),
    styles: accordionItemStyles
});
